import { Button, ButtonSize } from '@atlas/components';
import cx from 'classnames';
import { type FC, useEffect, useRef, useState } from 'react';
import { type RouteComponentProps, useRoute, useSearchParams } from 'wouter';

import CasinoGameBottomMenu from 'components/CasinoGameBottomMenu';
import DepositButton from 'components/DepositButton';
import ErrorEmptyState from 'components/EmptyStates/ErrorEmptyState';
import FavoriteButton from 'components/FavoriteButton';
import Icon from 'components/Icon/Icon';
import Loader from 'components/Loader';
import Logo from 'components/Logo';
import SubHeader from 'components/SubHeader/SubHeader';
import { useCasinoGames } from 'pages/CasinoPage/casino.context.tsx';
import {
  AuthStatus,
  SidebarSize,
  useClientProfile,
  useUISettings,
} from 'store';
import { AUTH_PAGES, useAuthRedirects } from 'utils/authRedirects';
import useLayout, { LayoutType } from 'utils/layout';
import { gamePagePathRegexp } from 'utils/location';
import { Translation } from 'utils/translations';
import { LoadingState } from 'utils/types';
import useIntl from 'utils/useIntl';

import useCasinoGame from './useCasinoGame.ts';

import styles from './CasinoGamePage.module.scss';

export type CasinoGamePageParams = {
  gameId: string;
};

export type CasinoGamePageProps = RouteComponentProps<CasinoGamePageParams>;

const CasinoGamePage: FC<CasinoGamePageProps> = ({ params }) => {
  const [isGamePage] = useRoute(gamePagePathRegexp);
  const layout = useLayout();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [fullScreenMode, setFullScreenMode] = useState(false);
  const { redirectToAuthPage } = useAuthRedirects();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === 'true';
  const providerName = searchParams.get('providerName') || undefined;
  const gameId = +params.gameId;
  const [{ status, data }] = useClientProfile();
  const { loading, gameUrl, gameData } = useCasinoGame(
    gameId,
    isDemo,
    providerName,
  );
  const { formatMoney } = useIntl();
  const [, { setLeftSidebar }] = useUISettings();
  const { loadFavoriteGames } = useCasinoGames();

  const isLoggedIn = status === AuthStatus.signedIn;
  const isLoggedOut = status === AuthStatus.signedOut;
  const isMobile = layout === LayoutType.mobile;

  const balance =
    typeof data?.wallet.balance === 'number'
      ? `${formatMoney(data.wallet.balance)}`
      : null;

  const bonus =
    typeof data?.wallet.bonuses[0]?.balance === 'number'
      ? `${formatMoney(data.wallet.bonuses[0].balance)}`
      : null;

  useEffect(() => {
    // isGamePage check is needed for case when the page has last time rendered
    // after direct navigation with history API (history.back() for example) and
    // location has already changed
    if (isLoggedOut && !isDemo && isGamePage) {
      redirectToAuthPage(AUTH_PAGES.signIn);
    }
  }, [redirectToAuthPage, isLoggedOut, isDemo, isGamePage]);

  useEffect(() => {
    if (fullScreenMode && wrapperRef.current?.requestFullscreen) {
      void wrapperRef.current.requestFullscreen();
    }
  }, [fullScreenMode]);

  useEffect(() => {
    setLeftSidebar(SidebarSize.collapsed);
  }, [setLeftSidebar]);

  useEffect(() => {
    const fullScreenChangeListener = () => {
      if (!document.fullscreenElement) {
        setFullScreenMode(false);
      }
    };

    document.addEventListener('fullscreenchange', fullScreenChangeListener);

    return () => {
      document.removeEventListener(
        'fullscreenchange',
        fullScreenChangeListener,
      );
    };
  }, []);

  useEffect(() => {
    loadFavoriteGames([gameId]);
  }, [loadFavoriteGames, gameId]);

  return (
    <div
      className={cx(styles['game-wrapper'], { [styles.mobile]: isMobile })}
      ref={wrapperRef}
    >
      <div
        className={cx(styles.container, {
          [styles['full-screen']]: fullScreenMode,
        })}
      >
        {fullScreenMode ? (
          <div className={styles['full-screen-header']}>
            <div className={styles.left}>
              <Logo />
              {balance && <div className={styles.balance}>{balance}</div>}
              {bonus && (
                <div className={styles.bonus}>
                  <Icon name="gift3.bold" width={16} height={16} />
                  {bonus}
                </div>
              )}
              {isLoggedIn ? (
                <DepositButton onClick={() => document.exitFullscreen()} />
              ) : (
                <Button
                  onClick={() => redirectToAuthPage(AUTH_PAGES.signUp)}
                  data-testid="registration-button"
                >
                  <Translation message="register_button" />
                </Button>
              )}
            </div>

            <div className={styles.right}>
              <FavoriteButton gameId={gameId} />
              <button
                type="button"
                className={styles.icon}
                data-testid="exit-full-screen-button"
                onClick={() => document.exitFullscreen()}
              >
                <Icon name="close.outline" width={24} height={24} />
              </button>
            </div>
          </div>
        ) : (
          <SubHeader>
            {gameData?.name && (
              <>
                <div
                  className={cx(styles['name-wrapper'], {
                    [styles.small]: gameData.name.length > 30,
                  })}
                >
                  <Icon name="casino" width={24} height={24} />
                  <span className={styles.name}>{gameData.name}</span>
                </div>
                {!isMobile && (
                  <>
                    {document.fullscreenEnabled && (
                      <button
                        type="button"
                        className={styles.icon}
                        data-testid="full-screen-button"
                        onClick={() => setFullScreenMode(true)}
                      >
                        <Icon name="export.outline" width={24} height={24} />
                      </button>
                    )}
                    <FavoriteButton gameId={gameId} />
                  </>
                )}
              </>
            )}
          </SubHeader>
        )}

        {loading === LoadingState.loading && <Loader />}
        {loading === LoadingState.success && gameUrl && (
          <>
            <iframe
              src={gameUrl}
              allowFullScreen
              title={gameData?.name}
              className={styles.frame}
            />
            {isDemo && !isMobile && (
              <div className={styles.demo}>
                <Translation message="demo_mode_title" />
                <Button
                  data-testid="demo-button"
                  onClick={() => {
                    setSearchParams((prev) => {
                      prev.delete('demo');
                      return prev;
                    });
                  }}
                  icon={<Icon name="play.outline" width={24} height={24} />}
                  size={ButtonSize.medium}
                >
                  <Translation message="demo_mode_button" />
                </Button>
              </div>
            )}
          </>
        )}
        {loading === LoadingState.failed && (
          <div className={styles['casino-game-page-error']}>
            <ErrorEmptyState />
          </div>
        )}
        {isMobile && <CasinoGameBottomMenu />}
      </div>
    </div>
  );
};

export default CasinoGamePage;
